import { routePaths } from 'components/Routes/data';
import { BulkUploadTypes, UploadTypeConfigCallback } from 'constants/bulkUpload';
import permissions from 'constants/permissions';
import { BulkUploadStagingDetailsSchemaContract } from 'schemas/BulkUploadStagingDetailsSchema';
import { dateFormats, getFormattedDateValue, getRunPayrollMonthList } from 'utils/Dates';
import { format } from 'date-fns';

const config: UploadTypeConfigCallback = (data) => {
  const getPayrollMonthOptions = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const payrollMonth = urlParams.get('payrollMonth') || '';

    if (!payrollMonth) {
      return [];
    }

    const monthList = getRunPayrollMonthList(payrollMonth);

    // Filter out current and future months - only show past months
    const filteredMonthList = monthList.filter((month) => {
      const monthDate = new Date(month);
      monthDate.setDate(1);
      monthDate.setHours(0, 0, 0, 0);

      const today = new Date();
      today.setDate(1);
      today.setHours(0, 0, 0, 0);

      return monthDate < today;
    });

    return filteredMonthList.map((month) => ({
      value: format(month, 'yyyy-MM-dd'),
      label: format(month, 'MMMM yyyy'),
    }));
  };
  const urlParams = new URLSearchParams(data.history.location.search);
  const payrollMonth = urlParams.get('payrollMonth') || '';

  const payrollMonthStr = getFormattedDateValue({
    date: payrollMonth,
    formatString: dateFormats.monthYear,
    filler: '',
  });

  const bulkUploadData = data?.stagingRecordData as
    | BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging']
    | undefined;
  const deductionComponent = bulkUploadData?.data?.[0]?.file_row?.deductions || {};
  const earningsComponent = bulkUploadData?.data?.[0]?.file_row?.earnings || {};

  return {
    permissions: [permissions.REPORTS_SALARY_REGISTER],
    initiation: {
      permissions: [],
      actionHeaderText: 'Update Salary Register',
      pageTitle: payrollMonthStr ? `for ${payrollMonthStr}` : '',
      step1Title: 'Download the Salary Register template',
      step1Subtitle:
        'Download the Salary Register template and open it in Excel. Fill in salary details, save the file, and upload it here.',
      instructions: [
        'Only upload past executed salary register',
        'There is a limit of 500 employees per upload',
        'Do not add or remove any columns.',
        "If a field is empty, leave it blank (don't enter '-', 'NA,' etc.).",
        'Mandatory fields are mentioned with * sign',
      ],
      hasMonthSelector: true,
      dropdownSelectorLabel: 'Select month',
      dropdownOptions: getPayrollMonthOptions(),
      onDropdownOptionChange(selectedMonth) {
        if (selectedMonth) {
          data.history.push({
            pathname: routePaths.bulkUploads.root,
            search: `?type=${BulkUploadTypes.TYPE_BULK_SALARY_REGISTER_UPDATE}&payrollMonth=${selectedMonth}`,
          });
        }
      },
      onSampleTemplateDownload(orgId, reportType) {
        window.open(
          `/v2/api/bulk-salary-register/download-template?payroll_month=${payrollMonth}`,
          '_blank',
        );
      },
    },
    preview: {
      showColumnsSelector: true,
      columns: [
        {
          title: 'ID',
          render: (item) => item?.file_row?.commonProperties?.employeeId,
        },
        {
          title: 'Name',
          render: (item) => item?.file_row?.commonProperties?.employeeName,
        },
        {
          title: 'Email ID',
          render: (item) => item?.file_row?.commonProperties?.email,
        },
        {
          title: 'Working Days',
          render: (item) => item?.file_row?.payable_days,
        },
        ...(Object.keys(earningsComponent).length > 0
          ? Object.keys(earningsComponent).map((earning) => ({
              title: earning,
              render: (
                item: BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging']['data'][0],
              ) => item?.file_row?.earnings?.[earning],
            }))
          : []),

        {
          title: 'Gross Salary(A)',
          render: (item) => item?.file_row?.gross_salary,
        },
        {
          title: 'Employee PF',
          render: (item) => item?.file_row?.employee_pf,
        },
        {
          title: 'Employee VPF',
          render: (item) => item?.file_row?.employee_vpf,
        },
        {
          title: 'Employee ESIC',
          render: (item) => item?.file_row?.employee_esic,
        },
        {
          title: 'Employee LWF',
          render: (item) => item?.file_row?.employee_lwf,
        },
        {
          title: 'Professional Tax',
          render: (item) => item?.file_row?.professional_tax,
        },
        {
          title: 'TDS',
          render: (item) => item?.file_row?.tds,
        },
        ...(Object.keys(deductionComponent).length > 0
          ? Object.keys(deductionComponent).map((deduction) => ({
              title: deduction,
              render: (
                item: BulkUploadStagingDetailsSchemaContract['bulk_uploads_staging']['data'][0],
              ) => item?.file_row?.deductions?.[deduction],
            }))
          : []),
        {
          title: 'Total Deductions(B)',
          render: (item) => item?.file_row?.total_deductions_b,
        },
        {
          title: 'Reimbursement(C)',
          render: (item) => item?.file_row?.reimbursement || '-',
        },
        {
          title: 'Advance Salary EMI(D)',
          render: (item) => item?.file_row?.advance_salary_emi || '-',
        },
        {
          title: 'Net Pay On Hold(E)',
          render: (item) => item?.file_row?.net_pay_on_hold || '-',
        },
        {
          title: 'Net Pay(A-B+C-D-E)',
          render: (item) => item?.file_row?.net_pay,
        },
        {
          title: 'Employer PF',
          render: (item) => item?.file_row?.employer_pf,
        },
        {
          title: 'PF Admin EDLI Charges',
          render: (item) => item?.file_row?.pf_admin_edli_charges,
        },
        {
          title: 'Employer ESIC',
          render: (item) => item?.file_row?.employer_esic,
        },
        {
          title: 'Employer LWF',
          render: (item) => item?.file_row?.employer_lwf,
        },
        {
          title: 'Leave Encashment',
          render: (item) => item?.file_row?.leave_encashment,
        },
        {
          title: 'Gratuity',
          render: (item) => item?.file_row?.gratuity,
        },
      ],
      renderConfirmationText: () =>
        `Almost done! Please confirm to proceed with the bulk salary register update process.`,
      successModalCtaText: 'View Updated Payroll',
      successModalMainText: 'Salary register succccessfully updated',
      successModalSubText: (successSubTextData) =>
        `You can view the ${successSubTextData?.payrollMonth} payroll details by going to the Run Payroll section.`,
      onSuccessModalCtaClick() {
        data.history.push(routePaths.phpRoutes.salaryRegister);
      },
      onDownloadErrorReport(history) {
        history?.push({
          pathname: routePaths.bulkUploads.initiate,
          search: `type=${BulkUploadTypes.TYPE_BULK_SALARY_REGISTER_UPDATE}`,
        });
      },
    },
  };
};

export default config;
